import { Injectable, inject, computed } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { ConfigService } from '@platform/core';
import { PrimeNG } from 'primeng/config';

/**
 * I18n Service
 *
 * Provides internationalization and localization functionality
 * Integrates with platform configuration and Transloco
 */
@Injectable({
  providedIn: 'root',
})
export class I18nService {
  private readonly transloco = inject(TranslocoService);
  private readonly configService = inject(ConfigService);
  private readonly primeng = inject(PrimeNG);

  // Supported locales from config
  readonly supportedLocales = computed(() => {
    const i18nConfig = this.configService.getI18n();
    return i18nConfig.supportedLocales || [];
  });

  // Default locale from config
  readonly defaultLocale = computed(() => {
    const i18nConfig = this.configService.getI18n();
    return i18nConfig.defaultLocale || 'en';
  });

  /**
   * Translate a key
   * @param key Translation key (supports nested keys with dot notation)
   * @param params Optional parameters for interpolation
   * @returns Translated string
   */
  translate(key: string, params?: Record<string, any>): string {
    return this.transloco.translate(key, params || {});
  }

  /**
   * Get current locale
   */
  getCurrentLocale(): string {
    return this.transloco.getActiveLang() || this.defaultLocale();
  }

  /**
   * Check if a locale is RTL
   * @param locale Locale code
   */
  isRtlLocale(locale: string): boolean {
    const i18nConfig = this.configService.getI18n();
    const rtlLanguages = i18nConfig.rtlLanguages || ['fa', 'ar', 'he', 'ur'];
    return rtlLanguages.includes(locale);
  }

  /**
   * Set locale
   * @param locale Locale code (e.g., 'en', 'fa', 'ar')
   */
  setLocale(locale: string): void {
    const supported = this.supportedLocales();
    if (supported.includes(locale)) {
      this.transloco.setActiveLang(locale);
      this.primeng.setTranslation(this.transloco.getTranslation('primeng'));
      // Update HTML lang attribute
      if (typeof document !== 'undefined') {
        document.documentElement.lang = locale;

        // Update direction for RTL languages
        const isRtl = this.isRtlLocale(locale);
        document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
        document.body.dir = isRtl ? 'rtl' : 'ltr';
        isRtl
          ? document.documentElement.classList.add('app-rtl')
          : document.documentElement.classList.remove('app-rtl');
      }

      // Persist locale preference
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('platform.locale', locale);
      }
    } else {
      console.warn(
        `Locale "${locale}" is not supported. Supported locales: ${supported.join(', ')}`
      );
    }
  }

  /**
   * Get supported locales
   */
  getSupportedLocales(): string[] {
    return this.supportedLocales();
  }

  /**
   * Check if a locale is supported
   */
  isLocaleSupported(locale: string): boolean {
    return this.supportedLocales().includes(locale);
  }

  /**
   * Format date according to current locale
   * @param date Date to format
   * @param format Optional format string (defaults to config dateFormat)
   */
  formatDate(date: Date | string | number, format?: string): string {
    const i18nConfig = this.configService.getI18n();
    const dateFormat = format || i18nConfig.dateFormat || 'YYYY-MM-DD';
    const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;

    // Use Intl.DateTimeFormat for locale-aware formatting
    const locale = this.getCurrentLocale();
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    };

    // Parse format string to Intl options
    if (dateFormat.includes('YYYY') || dateFormat.includes('yyyy')) {
      options.year = 'numeric';
    }
    if (dateFormat.includes('MM')) {
      options.month = '2-digit';
    }
    if (dateFormat.includes('DD') || dateFormat.includes('dd')) {
      options.day = '2-digit';
    }

    return new Intl.DateTimeFormat(locale, options).format(dateObj);
  }

  /**
   * Format time according to current locale
   * @param date Date to format
   * @param format Optional format string (defaults to config timeFormat)
   */
  formatTime(date: Date | string | number, format?: string): string {
    const i18nConfig = this.configService.getI18n();
    const timeFormat = format || i18nConfig.timeFormat || 'HH:mm:ss';
    const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;

    const locale = this.getCurrentLocale();
    const options: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      second: timeFormat.includes('ss') ? '2-digit' : undefined,
    };

    return new Intl.DateTimeFormat(locale, options).format(dateObj);
  }

  /**
   * Format currency according to current locale
   * @param amount Amount to format
   */
  formatCurrency(amount: number): string {
    const i18nConfig = this.configService.getI18n();
    const currency = i18nConfig.currency || 'USD';
    const locale = this.getCurrentLocale();

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
    }).format(amount);
  }

  /**
   * Format number according to current locale
   * @param value Number to format
   * @param options Optional Intl.NumberFormatOptions
   */
  formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
    const i18nConfig = this.configService.getI18n();
    const numberFormat = i18nConfig.numberFormat || 'en-US';
    const locale = this.getCurrentLocale();

    return new Intl.NumberFormat(locale, {
      ...options,
    }).format(value);
  }

  /**
   * Select translation stream (for reactive translations)
   * @param key Translation key
   * @param params Optional parameters
   */
  selectTranslate(key: string, params?: Record<string, any>) {
    return this.transloco.selectTranslate(key, params || {});
  }

  /**
   * Check if translation key exists
   * @param key Translation key
   */
  hasTranslation(key: string): boolean {
    const translation = this.transloco.translate(key);
    return translation !== key && translation !== '';
  }

  /**
   * Get current locale as observable
   */
  getCurrentLocale$() {
    return this.transloco.langChanges$;
  }
}
